/**
 * Locations admin index — picker for /admin/locations/:locationId.
 *
 * Lists the tenant's locations and routes the user into the per-location
 * admin view. Companion to LocationAdminView.tsx.
 *
 * RBAC: StaffRoute (Admin/Manager/Corporate).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ChevronRight, Loader2 } from 'lucide-react';
import ScorecardNavigation from '../ScorecardNavigation';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import managementService from '../../../services/managementService';
import { getTenantConnectionSummary } from '../../../services/squareConnectionService';

interface LocationLite {
  id: string;
  name: string;
}

function normalizeRole(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

const LocationsAdminIndex: React.FC = () => {
  const navigate = useNavigate();
  const { selectedTenant, tenantName } = useTenant();
  const { user } = useAuth();
  const [locations, setLocations] = useState<LocationLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [healthyIds, setHealthyIds] = useState<Set<string>>(new Set());

  const role = normalizeRole(user?.managementUser?.role);
  // Managers without an explicit location_id see the full tenant list;
  // Managers with a location_id are auto-routed into their own location admin view.
  const managerLocationId = role === 'Manager' ? user?.managementUser?.location_id : undefined;

  useEffect(() => {
    if (managerLocationId) {
      navigate(`/admin/locations/${managerLocationId}`, { replace: true });
    }
  }, [managerLocationId, navigate]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        let list: any[] | null = null;
        if (selectedTenant) {
          const r = await managementService.getLocationsByTenant(selectedTenant);
          if (r.success && r.locations) list = r.locations;
        }
        if (!list) {
          const r = await managementService.getLocations();
          if (r.success && r.locations) list = r.locations;
        }
        if (cancelled) return;
        setLocations(
          (list ?? []).map((l: any) => ({
            id: String(l.id ?? l.location_id),
            name: l.name ?? l.location_name ?? `Location ${l.id ?? l.location_id}`,
          })),
        );
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? 'Failed to load locations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedTenant]);

  // Fetch the tenant-wide Square connection summary in parallel so the
  // green Healthy pill can render next to each connected location. Failure
  // here is non-fatal — the list still renders, just without pills.
  useEffect(() => {
    if (!selectedTenant) {
      setHealthyIds(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { healthy_location_ids } = await getTenantConnectionSummary(selectedTenant);
        if (cancelled) return;
        setHealthyIds(new Set(healthy_location_ids));
      } catch (e) {
        if (!cancelled) setHealthyIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTenant]);

  const sorted = useMemo(
    () => [...locations].sort((a, b) => a.name.localeCompare(b.name)),
    [locations],
  );

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <button
          onClick={() => navigate('/restaurant/administration')}
          className="inline-flex items-center gap-1.5 text-sm text-inkMuted hover:text-ink mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Administration
        </button>

        <div className="mb-6">
          <h1 className="text-3xl font-display font-semibold text-ink">Locations</h1>
          <p className="text-sm text-inkMuted">
            Per-location settings · <span className="font-medium text-ink">{tenantName}</span>
          </p>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-inkMuted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading locations…
          </div>
        )}

        {error && (
          <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
            {error}
          </div>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div className="rounded border border-softBorder bg-white p-5 text-sm text-inkMuted">
            No locations to manage for this tenant.
          </div>
        )}

        {!loading && !error && sorted.length > 0 && (
          <ul className="divide-y divide-softBorder border border-softBorder rounded bg-white">
            {sorted.map((loc) => (
              <li key={loc.id}>
                <button
                  onClick={() => navigate(`/admin/locations/${loc.id}`)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-cream text-left"
                >
                  <span className="text-sm text-ink font-medium">{loc.name}</span>
                  <div className="flex items-center gap-2">
                    {healthyIds.has(loc.id) && (
                      <span
                        data-testid={`square-healthy-pill-${loc.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-900"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Square Connection · Healthy
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-inkMuted" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default LocationsAdminIndex;
